/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type { StatCell } from '@sportninja/common/features/playerFinder/aggregateSeasonStats'
import React from 'react'
import { font, weightBold } from 'src/components/css'

export interface StatTableProps {
  cells: StatCell[]
}

/**
 * Career-summary table: a row of abbreviation headers over a row of values,
 * split by a divider. Scrolls horizontally when there are more columns than
 * fit, so nothing is squeezed. Mirrors mobile's StatTable.
 */
const StatTable: React.FC<StatTableProps> = ({ cells }) => {
  if (cells.length === 0) {
    return null
  }
  return (
    <div css={scrollStyle}>
      <div css={contentStyle}>
        <div css={headerRowStyle}>
          {cells.map((cell) => (
            <span key={cell.abbr} css={[cellStyle, abbrStyle]}>
              {cell.abbr}
            </span>
          ))}
        </div>
        <div css={dividerStyle} />
        <div css={valueRowStyle}>
          {cells.map((cell) => (
            <span key={cell.abbr} css={[cellStyle, valueStyle]}>
              {cell.value}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

export default StatTable

/* Horizontal scroll lives here rather than on the modal body, so a wide stat
   set scrolls inside its own panel instead of widening the whole modal. */
const scrollStyle = css`
  overflow-x: auto;
  padding: 14px 8px;
`

/* Sizes to the widest row rather than the scroll viewport. Without this the
   divider (a block-level child) would only span the visible width and appear
   cut off once the table is scrolled. */
const contentStyle = css`
  width: max-content;
  min-width: 100%;
`

const headerRowStyle = css`
  display: flex;
  padding-bottom: 8px;
`

const valueRowStyle = css`
  display: flex;
  padding-top: 8px;
`

const dividerStyle = css`
  height: 1px;
  background-color: ${colors.SOFT_STEEL};
`

const cellStyle = css`
  min-width: 48px;
  padding: 0 8px;
  text-align: center;
  flex: 0 0 auto;
`

const abbrStyle = css`
  ${font.body}
  ${weightBold}
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
`

const valueStyle = css`
  ${font.title}
  ${weightBold}
  font-size: 16px;
  line-height: 20px;
  color: ${colors.WHITE};
`
